import { type FC, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Spin } from 'antd';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { CameraCardContainer } from '../components/camera-card';
import { DashboardControls } from '../components/DashboardControls';
import {
  CounterSettingsModal,
  StopCountingModal,
  StopAllSessionsModal,
} from '@smolt/shared/components';
import { AppHeader, AppFooter } from '@smolt/shared/components';
import { useCameraStore } from '@smolt/shared/store';
import { useShallow } from 'zustand/react/shallow';
import {
  useUpdateCameraSettings,
  useStartCounting,
  useStopCounting,
  useStopAllCounting,
  useStartAllCounting,
} from '../hooks/use-camera-data';
import { useAggregatedCameraStates } from '../hooks/use-aggregated-camera-states';
import type { CameraSettings } from '@smolt/shared/types';
import { useIsMobile, useBreakpoint } from '@smolt/shared/hooks';
import fishscopeLogo from '@/assets/FISHSCOPE_logo.png';
import akvakimLogo from '@/assets/AKVAKAM_logo_white 2.png';
import tidepoolLogo from '@/assets/tidepool_logo_dark_thick 2.png';

const { Content } = Layout;

export const DashboardPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const breakpoint = useBreakpoint();
  // useAggregatedCameraStates handles store sync internally
  const { cameras, isLoading } = useAggregatedCameraStates();

  // Get SessionDisplays and selector from store (synced by useAggregatedCameraStates)
  const SessionDisplays = useCameraStore((state) => state.SessionDisplays);
  const canStartAllSessions = useCameraStore((state) => state.canStartAllSessions());

  // Get focus camera ID from navigation state or sessionStorage (for hardware back button)
  const focusCameraId =
    location.state?.focusCameraId || sessionStorage.getItem('lastViewedCameraId');

  // Calculate initial slide index for Swiper
  const getInitialSlide = () => {
    if (focusCameraId && cameras) {
      const index = cameras.findIndex((cam) => cam.id === focusCameraId);
      return index !== -1 ? index : 0;
    }
    return 0;
  };
  const updateSettings = useUpdateCameraSettings();
  const startCounting = useStartCounting();
  const stopCounting = useStopCounting();
  const stopAllCounting = useStopAllCounting();
  const startAllCounting = useStartAllCounting();

  // Modal states and actions - consolidated selector with shallow comparison to reduce subscription overhead
  const {
    settingsModalOpen,
    stopModalOpen,
    stopAllSessionsModalOpen,
    selectedCameraId,
    openSettingsModal,
    closeSettingsModal,
    openStopModal,
    closeStopModal,
    openStopAllSessionsModal,
    closeStopAllSessionsModal,
  } = useCameraStore(
    useShallow((state) => ({
      settingsModalOpen: state.settingsModalOpen,
      stopModalOpen: state.stopModalOpen,
      stopAllSessionsModalOpen: state.stopAllSessionsModalOpen,
      selectedCameraId: state.selectedCameraId,
      openSettingsModal: state.openSettingsModal,
      closeSettingsModal: state.closeSettingsModal,
      openStopModal: state.openStopModal,
      closeStopModal: state.closeStopModal,
      openStopAllSessionsModal: state.openStopAllSessionsModal,
      closeStopAllSessionsModal: state.closeStopAllSessionsModal,
    }))
  );

  const selectedCamera = cameras?.find((cam) => cam.id === selectedCameraId);

  const handleSettingsSave = useCallback(
    (values: CameraSettings) => {
      if (selectedCameraId) {
        updateSettings.mutate({ id: selectedCameraId, settings: values });
        closeSettingsModal();
      }
    },
    [selectedCameraId, updateSettings, closeSettingsModal]
  );

  // Use ref to access latest SessionDisplays in callbacks without triggering re-renders
  const sessionDisplaysRef = useRef(SessionDisplays);
  sessionDisplaysRef.current = SessionDisplays;

  // Use refs to access latest modal states without causing handler recreation
  const modalStatesRef = useRef({ settingsModalOpen, stopModalOpen, stopAllSessionsModalOpen });
  modalStatesRef.current = { settingsModalOpen, stopModalOpen, stopAllSessionsModalOpen };

  // Handler cache to prevent recreation on every render
  const handlerCacheRef = useRef<
    Map<
      string,
      {
        onSettingsClick: () => void;
        onStartStop: () => void;
        onCardClick: () => void;
      }
    >
  >(new Map());

  // Create stable handler functions for each camera
  const createHandlers = useCallback(
    (cameraId: string) => {
      const cached = handlerCacheRef.current.get(cameraId);
      if (cached) return cached;

      const handlers = {
        onSettingsClick: () => openSettingsModal(cameraId),
        onStartStop: () => {
          // Use ref to access latest SessionDisplays without causing re-renders
          const sessionDisplay = sessionDisplaysRef.current.find((vm) => vm.id === cameraId);
          if (!sessionDisplay) return;

          if (sessionDisplay.isCounting || sessionDisplay.sessionState === 'starting') {
            openStopModal(cameraId);
          } else if (sessionDisplay.canStartSession) {
            startCounting.mutate(cameraId);
          }
        },
        onCardClick: () => {
          // Use ref to access latest modal states without causing handler recreation
          const { settingsModalOpen, stopModalOpen, stopAllSessionsModalOpen } =
            modalStatesRef.current;
          // Prevent navigation if any modal is open
          if (settingsModalOpen || stopModalOpen || stopAllSessionsModalOpen) {
            return;
          }
          navigate(`/counter/${cameraId}`);
        },
      };

      handlerCacheRef.current.set(cameraId, handlers);
      return handlers;
    },
    [openSettingsModal, openStopModal, startCounting, navigate]
  );

  // Build handler map with cached functions (only recreates when cameras array changes)
  const cameraHandlers = useMemo(() => {
    if (!cameras) {
      handlerCacheRef.current.clear();
      return new Map();
    }

    const currentIds = new Set(cameras.map((c) => c.id));

    // Remove stale handlers for cameras that no longer exist
    for (const id of handlerCacheRef.current.keys()) {
      if (!currentIds.has(id)) {
        handlerCacheRef.current.delete(id);
      }
    }

    // Create map with cached or new handlers
    const handlers = new Map();
    cameras.forEach((camera) => {
      handlers.set(camera.id, createHandlers(camera.id));
    });

    return handlers;
  }, [cameras, createHandlers]);

  const handleStopConfirm = useCallback(() => {
    if (selectedCameraId) {
      stopCounting.mutate(selectedCameraId);
      closeStopModal();
    }
  }, [selectedCameraId, stopCounting, closeStopModal]);

  const handleStopAll = useCallback(() => {
    const activeCameras = sessionDisplaysRef.current.filter(
      (vm) => vm.isCounting || vm.sessionState === 'starting'
    );
    if (activeCameras.length > 0) {
      openStopAllSessionsModal();
    }
  }, [openStopAllSessionsModal]);

  const handleStopAllConfirm = useCallback(() => {
    stopAllCounting.mutate();
    closeStopAllSessionsModal();
  }, [stopAllCounting, closeStopAllSessionsModal]);

  // Stabilize activeCameraNames to prevent unnecessary re-renders of DashboardControls
  const activeCameraNamesRef = useRef<string[]>([]);
  const activeCameraNames = useMemo(() => {
    const newNames = SessionDisplays.filter(
      (vm) => vm.isCounting || vm.sessionState === 'starting'
    ).map((vm) => vm.name);

    // Shallow comparison to check if names actually changed
    if (
      newNames.length === activeCameraNamesRef.current.length &&
      newNames.every((name, index) => name === activeCameraNamesRef.current[index])
    ) {
      return activeCameraNamesRef.current;
    }

    activeCameraNamesRef.current = newNames;
    return newNames;
  }, [SessionDisplays]);

  // Check if any camera is in transitioning state - uses SessionDisplays from store to avoid creating new instances
  const hasAnyTransitioningCamera = useMemo(() => {
    return SessionDisplays.some((vm) => vm.isTransitioning);
  }, [SessionDisplays]);

  const handleStartAll = useCallback(() => {
    startAllCounting.mutate();
  }, [startAllCounting]);

  // Cleanup navigation state and sessionStorage after component mount
  useEffect(() => {
    if (focusCameraId) {
      // Clear state to prevent re-execution on refresh
      window.history.replaceState({}, document.title);
      // Clear sessionStorage after focusing
      sessionStorage.removeItem('lastViewedCameraId');
    }
  }, [focusCameraId]);

  // Dynamic layout sizes based on breakpoint - memoized to prevent recalculation on every render
  const layoutConfig = useMemo(() => {
    switch (breakpoint) {
      case 'mobile':
        return {
          width: '100%',
          maxWidth: '100%',
          height: '100vh',
          gap: '16px',
          cardGap: '20px',
          buttonWidth: '128px',
          buttonHeight: '32px',
          buttonFontSize: '15px',
          marginTop: '22px',
        };
      case 'tablet':
        return {
          width: 'clamp(800px, 90vw, 1000px)',
          maxWidth: '1000px',
          height: 'clamp(500px, 80vh, 600px)',
          gap: '12px',
          cardGap: '16px',
          buttonWidth: 'clamp(128px, 12vw, 144px)',
          buttonHeight: 'clamp(32px, 3.5vh, 36px)',
          buttonFontSize: 'clamp(15px, 1.5vw, 16px)',
          marginTop: 'clamp(22px, 3vh, 28px)',
        };
      case 'desktop':
        return {
          width: 'clamp(1000px, 85vw, 1200px)',
          maxWidth: '1200px',
          height: 'clamp(600px, 85vh, 800px)',
          gap: '16px',
          cardGap: '20px',
          buttonWidth: 'clamp(144px, 12vw, 160px)',
          buttonHeight: 'clamp(36px, 4vh, 40px)',
          buttonFontSize: 'clamp(16px, 1.5vw, 18px)',
          marginTop: 'clamp(28px, 3.5vh, 32px)',
        };
      case 'largeDesktop':
        return {
          width: 'clamp(1400px, 95vw, 2400px)',
          maxWidth: '2400px',
          height: 'clamp(800px, 90vh, 1200px)',
          gap: '28px',
          cardGap: '40px',
          buttonWidth: 'clamp(200px, 16vw, 280px)',
          buttonHeight: 'clamp(56px, 6vh, 80px)',
          buttonFontSize: 'clamp(22px, 2vw, 28px)',
          marginTop: 'clamp(48px, 5vh, 64px)',
        };
      default:
        return {
          width: 'clamp(800px, 90vw, 1200px)',
          maxWidth: '1200px',
          height: 'clamp(500px, 80vh, 800px)',
          gap: '16px',
          cardGap: '20px',
          buttonWidth: '128px',
          buttonHeight: '32px',
          buttonFontSize: '15px',
          marginTop: '32px',
        };
    }
  }, [breakpoint]);

  const settingsInitialValues = useMemo(() => {
    if (!selectedCamera) return undefined;
    return {
      cameraName: selectedCamera.name,
      weightPerFish: selectedCamera.weightPerFish,
      inputTank: selectedCamera.inputTank,
      outputTank: selectedCamera.outputTank,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only tracking specific properties to avoid unnecessary re-renders
  }, [
    selectedCamera?.name,
    selectedCamera?.weightPerFish,
    selectedCamera?.inputTank,
    selectedCamera?.outputTank,
  ]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: '#48484A',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Calculate header and footer heights for mobile viewport
  // Header: clamp(36px, 8vh, 48px), Footer: clamp(16px, 4vh, 28px)
  const mobileHeaderHeight = 'clamp(36px, 8vh, 48px)';
  const mobileFooterHeight = 'clamp(16px, 4vh, 28px)';

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: isMobile ? 'flex-start' : 'center',
        minHeight: '100vh',
        height: isMobile ? '100vh' : 'auto',
        background: '#48484A',
        padding: isMobile ? '0' : '0',
        paddingTop: isMobile ? mobileHeaderHeight : '32px',
        paddingBottom: isMobile ? mobileFooterHeight : '0',
        overflow: isMobile ? 'hidden' : 'auto',
        boxSizing: 'border-box',
      }}
    >
      <Layout
        style={{
          width: layoutConfig.width,
          maxWidth: layoutConfig.maxWidth,
          height: isMobile ? '100%' : layoutConfig.height,
          maxHeight: isMobile ? '100%' : '90vh',
          background: '#48484A',
          minHeight: isMobile ? '0' : '500px',
          display: isMobile ? 'flex' : 'block',
          flexDirection: isMobile ? 'column' : undefined,
          overflow: isMobile ? 'hidden' : 'auto',
        }}
      >
        {/* Header */}
        <AppHeader
          title="Counter"
          logoSrc={fishscopeLogo}
        />

        {/* Pagination dots - Mobile only (≤ 799px) - Below Header */}
        {isMobile && (
          <div
            style={{
              background: '#48484A',
              padding: 'clamp(4px, 1vh, 8px) 20px',
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            <div
              className="swiper-pagination-custom"
              style={{ position: 'static' }}
            />
          </div>
        )}

        <Content
          style={{
            padding: isMobile ? '0' : `${layoutConfig.gap}px ${parseInt(layoutConfig.gap) * 1.5}px`,
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: '0',
            overflow: isMobile ? 'hidden' : 'auto',
            justifyContent: 'flex-start',
            height: isMobile ? '100%' : 'calc(100%)',
          }}
        >
          {isMobile ? (
            // Mobile Carousel Layout (≤ 799px) - No scroll, fit to screen
            <>
              {/* Card Area - Flex grow to fill available space */}
              <div
                style={{
                  flex: '1 1 auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '0',
                  padding: 'clamp(8px, 2vh, 20px) 0 0 0',
                  overflow: 'hidden',
                }}
              >
                <Swiper
                  modules={[Pagination]}
                  spaceBetween={20}
                  slidesPerView={1.6}
                  centeredSlides={true}
                  initialSlide={getInitialSlide()}
                  speed={400}
                  pagination={{
                    el: '.swiper-pagination-custom',
                    clickable: true,
                    bulletClass: 'swiper-pagination-bullet-custom',
                    bulletActiveClass: 'swiper-pagination-bullet-active-custom',
                  }}
                  style={{
                    width: 'calc(100% + 80px)',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: '-40px',
                    marginRight: '-40px',
                  }}
                >
                  {cameras?.map((camera) => (
                    <SwiperSlide
                      key={camera.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        position: 'relative',
                        transition: 'all 0.4s ease',
                      }}
                    >
                      {/* Gray overlay for non-active slides */}
                      <div
                        className="swiper-slide-overlay"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#8E8E93',
                          zIndex: 10,
                          pointerEvents: 'auto',
                          transition: 'opacity 0.4s ease',
                          borderRadius: '16px',
                        }}
                      />
                      <CameraCardContainer
                        cameraId={camera.id}
                        onSettingsClick={
                          cameraHandlers.get(camera.id)?.onSettingsClick || (() => {})
                        }
                        onStartStop={cameraHandlers.get(camera.id)?.onStartStop || (() => {})}
                        onCardClick={cameraHandlers.get(camera.id)?.onCardClick}
                        isMobile={true}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              {/* Mobile Control Buttons - Fixed at bottom */}
              <DashboardControls
                canStartAllSessions={canStartAllSessions}
                activeCameraNames={activeCameraNames}
                hasAnyTransitioningCamera={hasAnyTransitioningCamera}
                onStartAll={handleStartAll}
                onStopAll={handleStopAll}
                layoutConfig={layoutConfig}
                isMobile={true}
              />
            </>
          ) : (
            // Desktop Grid Layout (≥ 800px) - Responsive grid with scroll support
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: layoutConfig.cardGap,
                  width: '100%',
                  padding: '3% 3% 0 3%',
                  flexShrink: 0,
                }}
              >
                {cameras?.map((camera) => (
                  <CameraCardContainer
                    key={camera.id}
                    cameraId={camera.id}
                    onSettingsClick={cameraHandlers.get(camera.id)?.onSettingsClick || (() => {})}
                    onStartStop={cameraHandlers.get(camera.id)?.onStartStop || (() => {})}
                    onCardClick={cameraHandlers.get(camera.id)?.onCardClick}
                    isMobile={false}
                  />
                ))}
              </div>

              {/* Desktop Control Buttons */}
              <DashboardControls
                canStartAllSessions={canStartAllSessions}
                activeCameraNames={activeCameraNames}
                hasAnyTransitioningCamera={hasAnyTransitioningCamera}
                onStartAll={handleStartAll}
                onStopAll={handleStopAll}
                layoutConfig={layoutConfig}
                isMobile={false}
              />
            </>
          )}
        </Content>

        {/* Modals */}
        {selectedCamera && (
          <CounterSettingsModal
            open={settingsModalOpen}
            cameraName={selectedCamera.name}
            initialValues={settingsInitialValues}
            onSave={handleSettingsSave}
            onCancel={closeSettingsModal}
          />
        )}

        {selectedCamera && (
          <StopCountingModal
            open={stopModalOpen}
            cameraName={selectedCamera.name}
            onConfirm={handleStopConfirm}
            onCancel={closeStopModal}
          />
        )}

        <StopAllSessionsModal
          open={stopAllSessionsModalOpen}
          cameraNames={activeCameraNames}
          onConfirm={handleStopAllConfirm}
          onCancel={closeStopAllSessionsModal}
        />

        {/* Footer */}
        <AppFooter
          akvakimLogoSrc={akvakimLogo}
          tidepoolLogoSrc={tidepoolLogo}
        />
      </Layout>
    </div>
  );
};
